import * as React from 'react';
import '../../App.css';
import { graphql } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import { RvPanel } from '../Panel';
import { SupportedBrowsers } from '../SupportedBrowsers';
import gql from 'graphql-tag';
import { AppLogo } from './AppLogo';
import { cache } from '../..';
import { LeapFirmProvisionStatusType, GetLeapFirmProvisionStatusType } from '../Leap/LeapFirmProvisionStatusType';
import TimerIcon from '@material-ui/icons/Timer';
import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    Tooltip,
    Typography,
    makeStyles,
    createStyles,
    Theme,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import { TimerContext } from '../../contexts/TimerContext';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';
import PostAddIcon from '@material-ui/icons/PostAdd';
import HeadsetMicOutlinedIcon from '@material-ui/icons/HeadsetMicOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import WidgetsOutlinedIcon from '@material-ui/icons/WidgetsOutlined';
import { retrieveTimerSummaryCountData, TimerSummaryData, TimerSummaryParams } from '../../timesheetTimer/TimesheetTimerRepository';
import { showNotification } from '../../App';
import { DockMode } from '../MainDrawer';
import { UserCardV2 } from './UserCardV2';
import { GetSmokeballFirmProvisionStatusType, SmokeballFirmProvisionStatusType } from '../smokeball/SmokeballFirmProvisionStatusType';
import { ActionStepFirmProvisionStatusType, GetActionStepFirmProvisionStatusType } from '../actionStep/ActionStepFirmProvisionStatusType';
import { SelfHelp } from './SelfHelp';
import { NotificationCenter } from './NotificationCenter';
import { AffinityFirmProvisionStatusType, GetAffinityFirmProvisionStatusType } from '../affinity/AffinityFirmProvisionStatusType';
import { mainTheme } from '../../Theme';

// ─── Layout (MUI) ──────────────────────────────────────────────────────────

const useHeaderStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '64px',
            padding: '0 34px',
            [theme.breakpoints.down('sm')]: {
                padding: '0 12px',
            },
        },
        left: {
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            gap: 14,
        },
        logoWrapper: {
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('xs')]: {
                display: 'none',
            },
        },
        divider: {
            height: 26,
            margin: 0,
            alignSelf: 'center',
        },
        tenantTitle: {
            color: mainTheme.TextColor.Secondary,
            fontSize: 17,
            fontWeight: 400,
            lineHeight: 1,
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            [theme.breakpoints.down('sm')]: {
                display: 'none',
            },
        },
        right: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
        },
        actionDivider: {
            height: 22,
            margin: 0,
            alignSelf: 'center',
        },
    }),
);

interface HeaderLayoutProps {
    appMenu: React.ReactNode;
    logo: React.ReactNode;
    tenantTitle?: string;
    actions: Array<React.ReactNode>;
}

const HeaderLayout: React.FC<HeaderLayoutProps> = ({ appMenu, logo, tenantTitle, actions }) => {
    const classes = useHeaderStyles();
    const visibleActions = actions.filter(Boolean);
    return (
        <Box className={classes.root}>
            <Box className={classes.left}>
                {appMenu}
                <Box className={classes.logoWrapper}>{logo}</Box>
                {tenantTitle && (
                    <>
                        <Divider orientation="vertical" flexItem className={classes.divider} />
                        <Typography component="span" className={classes.tenantTitle}>{tenantTitle}</Typography>
                    </>
                )}
            </Box>
            <Box className={classes.right}>
                {visibleActions.map((action, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <Divider orientation="vertical" flexItem className={classes.actionDivider} />}
                        {action}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

interface AppHeaderProps {
    tenantId: string;
    isAuthenticated: boolean;
    onToggleMenu?: (showMenu: boolean) => void;
    isForceToChangePass?: boolean;
    onUploadDocuments?: () => void;
    onRequestSupport?: () => void;
    onClickInstructMatter?: () => void;
    onToggleDrawer?: () => void;
    onToggleQuickView?: () => void;
    dockMode?: DockMode;
    onCycleDock?: () => void;
    onCloseDock?: () => void;
    isMenuOpen?: boolean;
}

interface Tenant {
    companyName: string;
    title: string;
    logo: string;
    companyUrl?: string;
    sourceSystem: string;
    leapProvisioningStatus?: string;
    showResetLeapAccessCard?: boolean;
    smokeballProvisioningStatus?: string;
    showResetSmokeballAccessCard?: boolean;
    actionStepProvisioningStatus?: string;
    showResetActionStepAccessCard?: boolean;
    affinityProvisioningStatus?: string;
    showResetAffinityAccessCard?: boolean;
}

// TODO: make this a generic type to extend
interface QueryResult {
    loading?: boolean;
    networkStatus?: number;
    error?: ApolloError;
    tenant?: Tenant;
}

interface QAppHeaderProps extends AppHeaderProps {
    id?: string;
    data?: QueryResult;
}

// var tenantId = sessionStorage.getItem('tenantId');

const SupportedBrowserDrawer = () => (
    <RvPanel
        iconKey="info circle"
        size="small"
    >
        <SupportedBrowsers />
    </RvPanel>
);

interface AppBarState {
    showMenu?: boolean;
}

class AppBar extends React.Component<QAppHeaderProps, AppBarState> {

    static contextType = TimerContext;

    constructor(props: QAppHeaderProps, state: AppBarState) {
        super(props, state);
        this.toggleMenuVisibility = this.toggleMenuVisibility.bind(this);
        this.handleSidebarHide = this.handleSidebarHide.bind(this);
        this.onClickUploadDocument = this.onClickUploadDocument.bind(this);
        this.onClickSupport = this.onClickSupport.bind(this);
        this.onClickInstructMatter = this.onClickInstructMatter.bind(this);
        this.fetchTimerSummary = this.fetchTimerSummary.bind(this);
        this.onRetrieveTimerSummary = this.onRetrieveTimerSummary.bind(this);
        this.onToggleDrawer = this.onToggleDrawer.bind(this);
        this.onToggleQuickView = this.onToggleQuickView.bind(this);
        this.onMenuHoverEnter = this.onMenuHoverEnter.bind(this);
        this.state = {
            showMenu: false,
        };

        if (this.props.isAuthenticated) {
            this.fetchTimerSummary();
        }
    }

    render() {
        const { totalTimer } = this.context;

        let title = this.props.data && this.props.data.tenant ? this.props.data.tenant.title : undefined;

        let leapProvisioningStatus = LeapFirmProvisionStatusType.DoNotProvision;
        let smokeballProvisioningStatus = SmokeballFirmProvisionStatusType.DoNotProvision;
        let actionStepProvisioningStatus = ActionStepFirmProvisionStatusType.DoNotProvision;
        let affinityProvisioningStatus = AffinityFirmProvisionStatusType.DoNotProvision;

        let sourceSystem = '';
        if (this.props.data && this.props.data.tenant) {
            sourceSystem = this.props.data.tenant.sourceSystem && this.props.data.tenant.sourceSystem.toLocaleLowerCase();

            if (sourceSystem === 'leap' && this.props.data.tenant.leapProvisioningStatus) {
                // tslint:disable-next-line:max-line-length
                leapProvisioningStatus = GetLeapFirmProvisionStatusType(this.props.data.tenant.leapProvisioningStatus);
            }
            if (sourceSystem === 'smokeball' && this.props.data.tenant.smokeballProvisioningStatus) {
                // tslint:disable-next-line:max-line-length
                smokeballProvisioningStatus = GetSmokeballFirmProvisionStatusType(this.props.data.tenant.smokeballProvisioningStatus);
            }
            if (sourceSystem === 'actionstep' && this.props.data.tenant.actionStepProvisioningStatus) {
                // tslint:disable-next-line:max-line-length
                actionStepProvisioningStatus = GetActionStepFirmProvisionStatusType(this.props.data.tenant.actionStepProvisioningStatus);
            }
            if (sourceSystem === 'affinity' && this.props.data.tenant.affinityProvisioningStatus) {
                // tslint:disable-next-line:max-line-length
                affinityProvisioningStatus = GetAffinityFirmProvisionStatusType(this.props.data.tenant.affinityProvisioningStatus);
            }
        }

        cache.writeData({
            data: {
                leapProvisioningStatus: leapProvisioningStatus,
                smokeballProvisioningStatus: smokeballProvisioningStatus,
                actionStepProvisioningStatus: actionStepProvisioningStatus,
                affinityProvisioningStatus: affinityProvisioningStatus,
                sourceSystem: sourceSystem,
            },
        });

        const isMenuActive = !!(this.props.isMenuOpen || (this.props.dockMode && this.props.dockMode !== 'closed'));

        let appMenu =
            !this.props.isAuthenticated ||
            (leapProvisioningStatus !== LeapFirmProvisionStatusType.DoNotProvision && leapProvisioningStatus !== LeapFirmProvisionStatusType.Complete) ||
            (smokeballProvisioningStatus !== SmokeballFirmProvisionStatusType.DoNotProvision && smokeballProvisioningStatus !== SmokeballFirmProvisionStatusType.Complete) ||
            (actionStepProvisioningStatus !== ActionStepFirmProvisionStatusType.DoNotProvision && actionStepProvisioningStatus !== ActionStepFirmProvisionStatusType.Complete) ||
            (affinityProvisioningStatus !== AffinityFirmProvisionStatusType.DoNotProvision && affinityProvisioningStatus !== AffinityFirmProvisionStatusType.Complete)
                ? null
                : (
                    <IconButton
                        size="small"
                        className="rv-header-action-hover"
                        style={{
                            color: mainTheme.TemplateColor.Primary,
                            backgroundColor: isMenuActive ? mainTheme.CommonColor.LightBlue2 : undefined,
                        }}
                        onClick={this.toggleMenuVisibility}
                        onMouseEnter={this.onMenuHoverEnter}
                        aria-label={isMenuActive ? 'Close menu' : 'Open menu'}
                    >
                        {isMenuActive ? <MenuOpenIcon /> : <MenuIcon />}
                    </IconButton>
                );

        let timerButton = this.props.isAuthenticated && UserFeature.HasAccess(UserFeatureEnum.hasTimesheetTimer) && (
            <Tooltip title="Timesheet Timers">
                {/* Explicit bug report: this installed @material-ui/core version's own Badge
                    still defaults overlap to the deprecated "rectangle" string when not passed
                    explicitly - "rectangular" is required to silence that deprecation warning. */}
                <Badge badgeContent={totalTimer} color="error" overlap="rectangular">
                    <IconButton aria-label="timer" size="small" className="rv-header-action-hover" style={{ color: mainTheme.TemplateColor.Primary }} onClick={this.onToggleDrawer}>
                        <TimerIcon />
                    </IconButton>
                </Badge>
            </Tooltip>
        );

        let documentButton = this.props.isAuthenticated && UserFeature.HasAccess(UserFeatureEnum.hasUploadMatterDocument) && (
            <Tooltip title="Upload Document">
                <IconButton aria-label="upload document" size="small" className="rv-header-action-hover" style={{ color: mainTheme.TemplateColor.Primary }} onClick={this.onClickUploadDocument}>
                    <PostAddIcon />
                </IconButton>
            </Tooltip>
        );

        let instructMatterButton = this.props.isAuthenticated && UserFeature.HasAccess(UserFeatureEnum.hasDebtCollection) && (
            <Tooltip title="New Matter Instructions">
                <IconButton aria-label="new matter instructions" size="small" className="rv-header-action-hover" style={{ color: mainTheme.TemplateColor.Primary }} onClick={this.onClickInstructMatter}>
                    <DescriptionOutlinedIcon />
                </IconButton>
            </Tooltip>
        );

        let quickViewButton = this.props.isAuthenticated && UserFeature.HasAccess(UserFeatureEnum.hasQuickView) && (
            <Tooltip title="Quick View">
                <IconButton aria-label="quick view" size="small" className="rv-header-action-hover" style={{ color: mainTheme.TemplateColor.Primary }} onClick={this.onToggleQuickView} data-tour="quick-view-button">
                    <WidgetsOutlinedIcon />
                </IconButton>
            </Tooltip>
        );

        let notificationCenterButton = this.props.isAuthenticated
            && UserFeature.HasAccess(UserFeatureEnum.hasNotificationCenterFeatureUpdates)
            && <NotificationCenter />;

        let supportButton = (
            <Tooltip title="Contact Support">
                <Button
                    size="small"
                    className="rv-header-action-hover"
                    style={{ color: mainTheme.TemplateColor.Primary, textTransform: 'none', fontSize: '13px' }}
                    aria-label="support"
                    onClick={this.onClickSupport}
                    startIcon={<HeadsetMicOutlinedIcon style={{ fontSize: 18 }} />}
                >
                    Get Support
                </Button>
            </Tooltip>
        );

        let showResetLeapAccessCard: boolean = false;
        let showResetSmokeballAccessCard: boolean = false;
        let showResetActionStepAccessCard: boolean = false;
        let showResetAffinityAccessCard: boolean = false;

        if (sourceSystem === 'leap' && this.props.data && this.props.data.tenant && this.props.data.tenant.showResetLeapAccessCard) {
            showResetLeapAccessCard = this.props.data.tenant.showResetLeapAccessCard;
        } else if (sourceSystem === 'smokeball' && this.props.data && this.props.data.tenant && this.props.data.tenant.showResetSmokeballAccessCard) {
            showResetSmokeballAccessCard = this.props.data.tenant.showResetSmokeballAccessCard;
        } else if (sourceSystem === 'actionstep' && this.props.data && this.props.data.tenant && this.props.data.tenant.showResetActionStepAccessCard) {
            showResetActionStepAccessCard = this.props.data.tenant.showResetActionStepAccessCard;
        } else if (sourceSystem === 'affinity' && this.props.data && this.props.data.tenant && this.props.data.tenant.showResetAffinityAccessCard) {
            showResetAffinityAccessCard = this.props.data.tenant.showResetAffinityAccessCard;
        }

        const userCardv2 = !this.props.isAuthenticated ? (
            <SupportedBrowserDrawer />
        ) : (
            <UserCardV2
                isForceToChangePass={this.props.isForceToChangePass}
                showResetLeapAccess={showResetLeapAccessCard}
                showResetSmokeballAccess={showResetSmokeballAccessCard}
                showResetActionStepAccess={showResetActionStepAccessCard}
                showResetAffinityAccess={showResetAffinityAccessCard}
                tenantId={this.props.tenantId}
                // Raw (original-casing) value for display - unlike the `sourceSystem` local
                // above, which is lowercased specifically for the leap/smokeball/actionstep/
                // affinity comparisons and shouldn't be reused here.
                sourceSystem={this.props.data && this.props.data.tenant ? this.props.data.tenant.sourceSystem : undefined}
            />
        );

        const selfHelp = this.props.isAuthenticated ? (
            <div>
                <SelfHelp />
            </div>
        ) : null;

        const logoStorageKey: string = this.props.tenantId + '_brandlogo';
        // tslint:disable-next-line:no-any
        let logo: any = this.props.data && this.props.data.tenant ? this.props.data.tenant.logo : undefined;
        if (!this.props.isAuthenticated) {
            // tslint:disable-next-line:no-any
            let brandLogo: any = localStorage.getItem(logoStorageKey);
            if (brandLogo !== null && brandLogo.length > 0) {
                logo = brandLogo;
            }
        } else {
            if (logo) {
                localStorage.setItem(logoStorageKey, logo);
            }
        }

        let logoUrl = this.props.data && this.props.data.tenant && this.props.data.tenant.companyUrl ? this.props.data.tenant.companyUrl : 'http://www.RedRainCorp.com';

        return (
            <HeaderLayout
                appMenu={appMenu}
                logo={<AppLogo logoUrl={logoUrl} logoImg={logo} />}
                tenantTitle={title}
                actions={[
                    instructMatterButton,
                    documentButton,
                    timerButton,
                    quickViewButton,
                    notificationCenterButton,
                    selfHelp,
                    supportButton,
                    userCardv2,
                ]}
            />
        );
    }

    private toggleMenuVisibility() {
        if (this.props.dockMode && this.props.dockMode !== 'closed') {
            if (this.props.onCloseDock) { this.props.onCloseDock(); }
            this.setState({ showMenu: false });
            return;
        }
        var show = !(this.props.isMenuOpen || false);
        if (this.props.onToggleMenu) {
            this.props.onToggleMenu(show);
        }
        this.setState({ showMenu: show });
    }

    private handleSidebarHide(value: boolean) {
        this.setState({ showMenu: value });
        if (this.props.onToggleMenu) {
            this.props.onToggleMenu(value);
        }
    }

    private onMenuHoverEnter() {
        if ((!this.props.dockMode || this.props.dockMode === 'closed') && !this.props.isMenuOpen) {
            this.setState({ showMenu: true });
            if (this.props.onToggleMenu) {
                this.props.onToggleMenu(true);
            }
        }
    }

    private onClickUploadDocument() {
        if (this.props.onUploadDocuments) {
            this.props.onUploadDocuments();
        }
    }

    private onToggleDrawer() {
        if (this.props.onToggleDrawer) {
            this.props.onToggleDrawer();
        }
    }

    private onToggleQuickView() {
        if (this.props.onToggleQuickView) {
            this.props.onToggleQuickView();
        }
    }

    private onClickSupport() {
        if (this.props.onRequestSupport) {
            this.props.onRequestSupport();
        }
    }

    private onClickInstructMatter() {
        if (this.props.onClickInstructMatter) {
            this.props.onClickInstructMatter();
        }
    }

    private fetchTimerSummary() {
        const timerParams: TimerSummaryParams = {
            filter: '',
            first: 1000,
            offset: 0,
        };

        retrieveTimerSummaryCountData(
            timerParams,
            true,
            (data: TimerSummaryData) => this.onRetrieveTimerSummary(data),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');
            },
        );
    }

    private onRetrieveTimerSummary(data: TimerSummaryData) {
        const { setInitialTimerTotal } = this.context;
        setInitialTimerTotal(data.timerSummary.recordCount);
    }
}

const TenantData = gql`
    query Tenant($tenantId: String) {
        tenant(tenantId: $tenantId) {
            tenantId
            title
            logo
            companyUrl
            sourceSystem
            leapProvisioningStatus
            showResetLeapAccessCard
            smokeballProvisioningStatus
            showResetSmokeballAccessCard
            actionStepProvisioningStatus
            showResetActionStepAccessCard
            showResetAffinityAccessCard
        }
    }
`;

// tslint:disable-next-line:no-any
export const AppHeader = graphql<any, AppHeaderProps, any>(TenantData, {
    options: ({ tenantId }) => ({
        variables: { tenantId: tenantId },
        fetchPolicy: 'cache-first'
    }),
})(AppBar);
